import { useTranslation } from 'react-i18next';

interface Translations {
  [languageCode: string]: {
    title?: string;
    description?: string;
    [key: string]: string | undefined;
  };
}

interface MetricDefinition {
  title?: string;
  description?: string;
  translations?: Translations;
  metricDefinition?: {
    id?: string;
    title?: string;
    translations?: Translations;
  };
  [key: string]: any;
}

/**
 * Custom hook to get translated metric definition fields
 * Follows fallback chain: currentLanguage → 'en' → original field → fallback
 */
export const useMetricTranslation = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  /**
   * Get translated field from metric definition
   * @param metricDefinition - The metric definition object
   * @param field - The field to translate (e.g., 'title', 'description')
   * @param fallback - Optional fallback value if all translations fail
   * @returns Translated value or fallback
   */
  const getTranslatedField = (
    metricDefinition: MetricDefinition | null | undefined,
    field: keyof Translations[string],
    fallback: string = ''
  ): string => {
    if (!metricDefinition) {
      return fallback;
    }

    // Check if metricDefinition object exists (new structure from backend)
    const translations = metricDefinition.metricDefinition?.translations || metricDefinition.translations;

    // Try current language
    if (translations?.[currentLanguage]?.[field]) {
      return translations[currentLanguage][field] as string;
    }

    // Try English as fallback
    if (currentLanguage !== 'en' && translations?.['en']?.[field]) {
      return translations['en'][field] as string;
    }

    // Try metricDefinition.title first (nested structure) - only check 'title' field
    if (field === 'title' && metricDefinition.metricDefinition?.title) {
      return metricDefinition.metricDefinition.title;
    }

    // Try original field on metric definition
    if (metricDefinition[field]) {
      return metricDefinition[field] as string;
    }

    // Return fallback
    return fallback;
  };

  /**
   * Get translated title
   */
  const getTranslatedTitle = (
    metricDefinition: MetricDefinition | null | undefined,
    fallback: string = ''
  ): string => {
    return getTranslatedField(metricDefinition, 'title', fallback);
  };

  /**
   * Get translated description
   */
  const getTranslatedDescription = (
    metricDefinition: MetricDefinition | null | undefined,
    fallback: string = ''
  ): string => {
    return getTranslatedField(metricDefinition, 'description', fallback);
  };

  return {
    getTranslatedField,
    getTranslatedTitle,
    getTranslatedDescription,
  };
};

export default useMetricTranslation;
